/**
 * Enhanced Simple Chat Widget for plain markdown streaming from xAI.
 * 
 * FEATURES:
 * - Progress indicators via SSE streaming (think messages)
 * - User feedback collection after responses
 * - Request ID tracking for progress/feedback correlation
 * - SIMPLIFIED SSE parser (backend now properly encodes multi-line content)
 * 
 * This version integrates:
 * - useXAIChatProgress hook for real-time progress
 * - XAIChatProgressIndicator for visual progress display
 * - XAIChatFeedbackPrompt for user feedback collection
 * 
 * SSE Parsing: Uses standard algorithm - trusts backend _format_sse() 
 * to properly encode newlines. No complex boundary detection needed.
 * 
 * @see workspace/CU-868h0m8ay for implementation guide
 * @see workspace/STREAMING_FIX_SUMMARY.md for SSE fix details
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, TextField, IconButton, Paper, Typography, CircularProgress, styled } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslucencePlanResultRootSummaryQuery } from '@app/api/carApi.manual';
import { useAlEnginePlanResultTypeRetrieveQuery } from '@app/api/carApi.generated';
import { polarisChatStyles, polarisTheme } from '../theme';
import { useXAIChatProgress } from '../hooks/useXAIChatProgress';
import { XAIChatProgressIndicator } from './XAIChatProgressIndicator';
import { XAIChatFeedbackPrompt } from './XAIChatFeedbackPrompt';

// Styled container for markdown content with Open Sans typography
const MarkdownContent = styled('div')(({ theme }) => ({
  fontFamily: "'Open Sans', sans-serif",
  '& p': {
    margin: theme.spacing(1, 0),
    lineHeight: 1.6,
    fontFamily: 'inherit',
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: theme.spacing(2, 0, 1, 0),
    fontWeight: 550,
    fontFamily: 'inherit',
  },
  '& h1': { fontSize: '1.5rem' },
  '& h2': { fontSize: '1.3rem' },
  '& h3': { fontSize: '1.15rem' },
  '& h4': { fontSize: '1rem' },
  '& ul, & ol': {
    margin: theme.spacing(1, 0),
    paddingLeft: theme.spacing(3),
    fontFamily: 'inherit',
  },
  '& li': {
    margin: theme.spacing(0.5, 0),
  },
  '& strong': {
    fontWeight: 600,
  },
  '& em': {
    fontStyle: 'italic',
  },
  '& code': {
    backgroundColor: polarisTheme.sunkFills,
    padding: '2px 6px',
    borderRadius: 4,
    fontFamily: 'monospace',
    fontSize: '0.9em',
  },
  '& pre': {
    backgroundColor: polarisTheme.sunkFills,
    padding: theme.spacing(1.5),
    borderRadius: polarisTheme.roundedL,
    overflow: 'auto',
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
  },
  '& blockquote': {
    borderLeft: `4px solid ${polarisTheme.strokeEmphasis}`,
    margin: theme.spacing(1, 0),
    paddingLeft: theme.spacing(2),
    color: polarisTheme.secondaryText,
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${polarisTheme.strokeDefault}`,
    margin: theme.spacing(2, 0),
  },
  '& a': {
    color: polarisTheme.linkText,
    textDecoration: 'underline',
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    margin: theme.spacing(1, 0),
  },
  '& th, & td': {
    border: `1px solid ${polarisTheme.strokeDefault}`,
    padding: theme.spacing(1),
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: polarisTheme.sunkFills,
    fontWeight: 600,
  },
}));

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  requestId?: string;  // Track request ID for feedback
}

interface SimpleChatWidgetProps {
  apiUrl: string;
  planResultRootId?: string;  // Required for feedback
  planResultTypeId?: string;  // Required for feedback
}

export const SimpleChatWidget: React.FC<SimpleChatWidgetProps> = ({ 
  apiUrl,
  planResultRootId,
  planResultTypeId,
}) => {
  // Phase 1B: Persistent thread_id for conversation tracking
  // Generate once on mount and reuse for all messages in this conversation
  const [threadId] = useState(() => crypto.randomUUID());

  // In-focus plan result type: from URL (props) initially, updated from SSE complete event after in-chat switch
  const [effectivePlanResultTypeId, setEffectivePlanResultTypeId] = useState<
    string | undefined
  >(planResultTypeId);

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [lastUserMessage, setLastUserMessage] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Progress hook - connects to SSE progress stream
  const {
    progress,
    lastThinkItem,
    isConnected: progressConnected,
    streamedText,
    lastCompletePlanResultTypeId,
    lastCompletePlanResultTypeLabel,
  } = useXAIChatProgress(currentRequestId);

  // Sync effective plan result type from URL (props) when user navigates
  useEffect(() => {
    setEffectivePlanResultTypeId(planResultTypeId);
  }, [planResultTypeId]);

  // Update effective plan result type from SSE complete event (e.g. after "switch to Primary")
  useEffect(() => {
    if (lastCompletePlanResultTypeId) {
      setEffectivePlanResultTypeId(lastCompletePlanResultTypeId);
    }
  }, [lastCompletePlanResultTypeId]);

  // Plan name: from plan result root summary API (for Polaris Chat header)
  const { data: planResultRootSummary } = useTranslucencePlanResultRootSummaryQuery(
    { planResultRootId: planResultRootId ?? '' },
    { skip: !planResultRootId }
  );
  const planName = planResultRootSummary?.name ?? null;

  // Scenario label: from API on load, or from complete event after in-chat switch
  const { data: planResultTypeData } = useAlEnginePlanResultTypeRetrieveQuery(
    { id: effectivePlanResultTypeId ?? '' },
    { skip: !effectivePlanResultTypeId }
  );
  const scenarioLabel =
    lastCompletePlanResultTypeLabel ?? planResultTypeData?.label ?? null;

  const isPolarisMode = apiUrl.includes("/api/polaris-chat");
  const displayedStreamingContent = isPolarisMode ? streamedText : streamingContent;

  // Debug logging
  useEffect(() => {
    if (lastThinkItem) {
      console.log("[SimpleChatWidget] Think item updated:", lastThinkItem.title);
    }
  }, [lastThinkItem]);

  // Polaris mode: the POST returns JSON immediately; content is streamed via progress SSE.
  // Finalize the assistant message when we receive a `complete` event.
  useEffect(() => {
    if (!isPolarisMode) return;
    if (!currentRequestId) return;
    if (!progress) return;

    if (progress.type === "complete") {
      const fullContent = progress.result?.content || streamedText || "";
      if (fullContent) {
        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: fullContent,
          requestId: currentRequestId,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      }
      setIsLoading(false);
      setStreamingContent("");
      setCurrentRequestId(null); // stop progress streaming
      abortControllerRef.current = null;
    } else if (progress.type === "error") {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `Error: ${progress.error}`,
        },
      ]);
      setIsLoading(false);
      setStreamingContent("");
      setCurrentRequestId(null); // stop progress streaming
      abortControllerRef.current = null;
    }
  }, [currentRequestId, isPolarisMode, progress, streamedText]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, displayedStreamingContent, scrollToBottom]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: inputValue.trim(),
    };

    // Generate request ID for this message exchange
    const requestId = crypto.randomUUID();

    setMessages(prev => [...prev, userMessage]);
    setLastUserMessage(userMessage.content);
    setInputValue('');
    setIsLoading(true);
    setStreamingContent('');
    // Note: Don't set currentRequestId yet for Polaris mode - wait until POST succeeds
    // XAI mode doesn't use SSE progress, so we can set it immediately
    if (!isPolarisMode) {
      setCurrentRequestId(requestId);
    }

    abortControllerRef.current = new AbortController();

    try {
      const body: Record<string, unknown> = {
        prompt: {
          role: 'user',
          content: userMessage.content,
          id: userMessage.id,
        },
        thread_id: threadId,
        response_id: requestId,
      };
      if (isPolarisMode && planResultRootId && effectivePlanResultTypeId) {
        body.plan_result_root_id = planResultRootId;
        body.plan_result_type_id = effectivePlanResultTypeId;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        // Try to parse error response body for friendly error messages
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
              // Add suggestion if available
              if (errorData.suggestion) {
                errorMessage += `\n\n${errorData.suggestion}`;
              }
            }
          }
        } catch (parseError) {
          // JSON parsing failed, use default error message
          console.warn('Failed to parse error response:', parseError);
        }
        
        throw new Error(errorMessage);
      }

      // Polaris mode: POST returns JSON with request_id; do not attempt to stream the POST body.
      if (isPolarisMode) {
        const data = (await response.json()) as { request_id?: string };
        const actualRequestId = data?.request_id || requestId;
        // Now that POST succeeded, start the SSE progress stream
        setCurrentRequestId(actualRequestId);
        // Keep loading until progress SSE sends `complete` / `error`.
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events (SIMPLIFIED - backend now properly encodes multi-line content)
        // 
        // IMPORTANT: This widget handles both XAI Chat and Polaris Chat SSE formats:
        // - XAI Chat: Uses `event: text` with plain `data:` chunks, separate progress stream
        // - Polaris Chat: May send `event: complete`, `event: connected`, `event: think_item` 
        //   in main stream - these control events are skipped, only content is rendered
        while (buffer.length > 0) {
          const eventEnd = buffer.indexOf('\n\n');
          if (eventEnd === -1) break;  // Wait for complete event

          const eventBlock = buffer.slice(0, eventEnd);
          buffer = buffer.slice(eventEnd + 2);

          if (!eventBlock.trim()) continue;  // Skip empty events

          // Extract event type and data from SSE format
          const lines = eventBlock.split('\n');
          let eventType = '';
          const dataLines: string[] = [];
          
          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              // Remove "data:" prefix and optional leading space
              const lineContent = line.slice(5);
              dataLines.push(lineContent.startsWith(' ') ? lineContent.slice(1) : lineContent);
            }
          }
          
          // Skip control events - they're metadata, not content to display
          // This ensures both XAI Chat and Polaris Chat formats work correctly:
          // - XAI Chat: Only sends `event: text` (no control events in main stream)
          // - Polaris Chat: May send `event: complete`, `event: connected`, `event: think_item`
          //   These should NOT be rendered as content
          if (eventType === 'complete' || eventType === 'error' || eventType === 'connected') {
            // These are control events, not content to display
            if (eventType === 'complete') {
              // Stream is done - mark as complete but continue processing buffer
              // in case there are any remaining content chunks before the complete event
              // The outer loop will exit when reader.read() returns done=true
              continue;
            }
            continue;
          }
          
          // Join data lines with newlines (SSE spec behavior)
          const content = dataLines.join('\n');
          
          if (content === '[DONE]') continue;
          
          // Remove any custom markdown tags if present
          const cleanContent = content.replace(/<\/?custommarkdown>/g, '');
          
          // Only process text events or events without explicit type (legacy format)
          // Skip JSON objects that are control messages
          if (cleanContent.startsWith('{') && (cleanContent.includes('"type"') || cleanContent.includes('"error"'))) {
            // This is a control message (complete, error, etc.), not content
            // We already handled these above via event type
            continue;
          }
          
          // Handle JSON-wrapped format (if backend changes to this in future)
          if (cleanContent.startsWith('{') && cleanContent.includes('"content"')) {
            try {
              const parsed = JSON.parse(cleanContent);
              if (parsed.content !== undefined && !parsed.type) {
                // Only extract content if it's not a control message
                accumulatedContent += parsed.content;
                setStreamingContent(accumulatedContent);
                continue;
              }
            } catch (e) {
              // Not JSON, treat as plain text
            }
          }

          // Regular text content (from event: text or no event type)
          // Only render actual content events - skip control events (already handled above)
          // This works for both:
          // - XAI Chat: `event: text` with content chunks
          // - Polaris Chat: `event: text` or plain `data:` with content chunks
          if (cleanContent && (eventType === 'text' || !eventType)) {
            accumulatedContent += cleanContent;
            setStreamingContent(accumulatedContent);
          }
        }
      }

      // Add assistant message when stream completes
      if (accumulatedContent) {
        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: accumulatedContent,
          requestId: requestId,  // Store for feedback
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Chat error:', error);
        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `Error: ${(error as Error).message}`,
        }]);
        
        // Clean up loading state immediately on error (for both modes)
        setIsLoading(false);
        setStreamingContent('');
        setCurrentRequestId(null);  // Stop progress streaming
        abortControllerRef.current = null;
      }
    } finally {
      // XAI mode streams the POST response body; once it ends, we can stop.
      // Polaris mode should keep the progress SSE connection alive until complete/error.
      // Note: catch block handles cleanup on error, so this only runs for successful flows
      if (!isPolarisMode) {
        setIsLoading(false);
        setStreamingContent('');
        setCurrentRequestId(null);  // Stop progress streaming
        abortControllerRef.current = null;
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      bgcolor: polarisChatStyles.containerBg,
      fontFamily: polarisChatStyles.fontFamily,
    }}>
      {/* Header: title + Plan name + Scenario (in focus) */}
      <Box sx={{ 
        p: polarisTheme.spacingM,
        borderBottom: `1px solid ${polarisTheme.strokeDefault}`,
        bgcolor: polarisTheme.containerFills,
        display: 'flex',
        flexDirection: 'column',
        gap: polarisTheme.spacingS,
      }}>
        <Typography 
          sx={{ 
            font: polarisTheme.fontHeadingSmall,
            color: polarisTheme.primaryText,
          }}
        >
          Polaris Chat
        </Typography>
        {isPolarisMode && (planResultRootId || effectivePlanResultTypeId) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: polarisTheme.spacingS }}>
            <Typography
              sx={{
                font: polarisTheme.fontBodySmall,
                color: polarisTheme.secondaryText,
              }}
            >
              Plan: {planName ?? '…'}
            </Typography>
            <Typography
              sx={{
                font: polarisTheme.fontBodySmall,
                color: polarisTheme.secondaryText,
              }}
            >
              Scenario: {scenarioLabel ?? '…'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Messages */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        p: polarisTheme.spacingL,
        display: 'flex',
        flexDirection: 'column',
        gap: polarisChatStyles.messageGap,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: polarisTheme.sunkFills,
          borderRadius: polarisTheme.roundedFull,
        },
        '&::-webkit-scrollbar-thumb': {
          background: polarisTheme.strokeEmphasis,
          borderRadius: polarisTheme.roundedFull,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: polarisTheme.strokeInteractiveElHover,
        },
      }}>
        {messages.map((msg) => (
          <Box key={msg.id}>
            <Paper
              elevation={0}
              sx={{
                p: polarisTheme.spacingM,
                maxWidth: '85%',
                alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                bgcolor: msg.role === 'user' 
                  ? polarisChatStyles.userBubble.backgroundColor
                  : polarisChatStyles.assistantBubble.backgroundColor,
                color: msg.role === 'user'
                  ? polarisChatStyles.userBubble.color
                  : polarisChatStyles.assistantBubble.color,
                borderRadius: polarisChatStyles.assistantBubble.borderRadius,
                boxShadow: polarisChatStyles.assistantBubble.boxShadow,
                transition: 'all 0.2s ease-in-out',
                animation: 'fadeIn 0.3s ease-out',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'translateY(10px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              {msg.role === 'assistant' ? (
                <MarkdownContent>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                </MarkdownContent>
              ) : (
                <Typography sx={{ 
                  fontFamily: polarisChatStyles.fontFamily,
                  color: polarisTheme.interactiveAccent,
                }}>
                  {msg.content}
                </Typography>
              )}
            </Paper>
            
            {/* Show feedback prompt for assistant messages */}
            {msg.role === 'assistant' && msg.requestId && planResultRootId && effectivePlanResultTypeId && (
              <XAIChatFeedbackPrompt
                planResultRootId={planResultRootId}
                planResultTypeId={effectivePlanResultTypeId}
                requestId={msg.requestId}
                query={lastUserMessage}
                response={msg.content}
              />
            )}
          </Box>
        ))}

        {/* Progress indicator (think messages) */}
        {progressConnected && lastThinkItem && (
          <XAIChatProgressIndicator
            thinkItem={lastThinkItem}
            isConnected={progressConnected}
          />
        )}

        {/* Streaming content (only while actively loading) */}
        {displayedStreamingContent && (!isPolarisMode || isLoading) && (
          <Paper
            elevation={0}
            sx={{
              p: polarisTheme.spacingM,
              maxWidth: '85%',
              alignSelf: 'flex-start',
              bgcolor: polarisChatStyles.assistantBubble.backgroundColor,
              color: polarisChatStyles.assistantBubble.color,
              borderRadius: polarisChatStyles.assistantBubble.borderRadius,
              boxShadow: polarisChatStyles.assistantBubble.boxShadow,
            }}
          >
            <MarkdownContent>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayedStreamingContent}</ReactMarkdown>
            </MarkdownContent>
            {isLoading && (
              <CircularProgress 
                size={16} 
                sx={{ ml: 1, color: polarisTheme.interactiveAccent }} 
              />
            )}
          </Paper>
        )}

        {/* Loading indicator when no content yet */}
        {isLoading && !displayedStreamingContent && !lastThinkItem && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: polarisTheme.spacingS,
            p: polarisTheme.spacingM,
            bgcolor: polarisChatStyles.assistantBubble.backgroundColor,
            borderRadius: polarisChatStyles.assistantBubble.borderRadius,
            boxShadow: polarisChatStyles.assistantBubble.boxShadow,
            maxWidth: '120px',
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.6 },
            },
          }}>
            <CircularProgress 
              size={16} 
              sx={{ color: polarisTheme.interactiveAccent }}
            />
            <Typography 
              sx={{ 
                font: polarisTheme.fontBodySmall,
                color: polarisTheme.secondaryText,
              }}
            >
              Thinking...
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box sx={{ 
        p: polarisTheme.spacingM, 
        borderTop: `1px solid ${polarisTheme.strokeDefault}`,
        bgcolor: polarisTheme.containerFills,
        display: 'flex',
        alignItems: 'flex-end',
        gap: polarisTheme.spacingS,
      }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
          disabled={isLoading}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: polarisTheme.roundedL,
              bgcolor: polarisTheme.sunkFills,
              fontFamily: polarisChatStyles.fontFamily,
              '& fieldset': {
                borderColor: polarisTheme.strokeInteractiveEl,
              },
              '&:hover fieldset': {
                borderColor: polarisTheme.strokeInteractiveElHover,
              },
              '&.Mui-focused fieldset': {
                borderColor: polarisTheme.interactiveAccent,
              },
            },
            '& .MuiInputBase-input': {
              fontFamily: polarisChatStyles.fontFamily,
            },
          }}
        />
        <IconButton 
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading}
          sx={{
            bgcolor: polarisTheme.interactiveAccent,
            color: polarisTheme.accentPrimaryText,
            borderRadius: polarisTheme.roundedClickable,
            '&:hover': {
              bgcolor: polarisTheme.interactiveAccentHover,
            },
            '&:active': {
              bgcolor: polarisTheme.interactiveAccentPressed,
            },
            '&.Mui-disabled': {
              bgcolor: polarisTheme.interactiveAccentDisabled,
              color: polarisTheme.accentDisabledText,
            },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default SimpleChatWidget;
